import { FC } from 'react';
import React from 'react';
import { asText, Content } from '@prismicio/client';
import {
  SliceComponentProps,
  PrismicRichText,
  PrismicLink,
} from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { VscArrowRight } from 'react-icons/vsc';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

/**
 * Props for `FeatureColumnsWithGraphic`.
 */
export type FeatureColumnsWithGraphicProps =
  SliceComponentProps<Content.FeatureColumnsWithGraphicSlice>;

// Legacy Code Graphic Component
const LegacyCodeGraphic: FC = () => {
  return (
    <div className="flex items-center justify-center h-64 bg-[#0E1010] rounded-lg border border-gray-700">
      <div className="text-center">
        <div className="text-[#05F273] text-6xl font-bold mb-4 font-mono transform -rotate-12">
          &lt;/&gt;
        </div>
        <div className="text-white text-xl font-semibold">Legacy code</div>
      </div>
    </div>
  );
};

// Velx Workflow Graphic Component
const VelxWorkflowGraphic: FC = () => {
  return (
    <div className="flex items-center justify-center h-64 bg-[#0E1010] rounded-lg border border-gray-700 relative">
      {/* Central Velx Logo */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="bg-[#05F273] text-black px-4 py-2 rounded-lg font-bold text-lg">
          Velx
        </div>
      </div>

      {/* Surrounding workflow nodes */}
      <div className="absolute top-8 left-8 bg-[#05F273] text-black px-3 py-1 rounded-full text-sm font-medium">
        PLAN
      </div>
      <div className="absolute top-8 right-8 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
        DESIGN
      </div>
      <div className="absolute bottom-8 left-8 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
        DEPLOY
      </div>
      <div className="absolute bottom-8 right-8 bg-[#05F273] text-black px-3 py-1 rounded-full text-sm font-medium">
        DEVELOP
      </div>

      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <defs>
          <pattern id="dots" patternUnits="userSpaceOnUse" width="4" height="4">
            <circle cx="2" cy="2" r="1" fill="#05F273" opacity="0.5" />
          </pattern>
        </defs>
        <line
          x1="25%"
          y1="25%"
          x2="50%"
          y2="50%"
          stroke="url(#dots)"
          strokeWidth="2"
        />
        <line
          x1="75%"
          y1="25%"
          x2="50%"
          y2="50%"
          stroke="url(#dots)"
          strokeWidth="2"
        />
        <line
          x1="25%"
          y1="75%"
          x2="50%"
          y2="50%"
          stroke="url(#dots)"
          strokeWidth="2"
        />
        <line
          x1="75%"
          y1="75%"
          x2="50%"
          y2="50%"
          stroke="url(#dots)"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
};

/**
 * Component for "FeatureColumnsWithGraphic" Slices.
 */
const FeatureColumnsWithGraphic: FC<FeatureColumnsWithGraphicProps> = ({
  slice,
}) => {
  const { section_heading, section_subheading, cta_link, features } =
    slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0E1010] py-10 px-4"
    >
      <div className="container mx-auto max-w-7xl text-center md:text-left lg:text-left">
        {/* Top Section - Heading and CTA */}
        {section_heading && (
          <div className="mb-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-antonio">
              <HighlightedText text={asText(section_heading) || ''} />
            </h2>
          </div>
        )}

        {section_subheading && (
          <div className="mb-4">
            <PrismicRichText
              field={section_subheading}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg text-gray-300 leading-relaxed">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        )}

        {cta_link && (
          <PrismicLink
            field={cta_link}
            className="inline-flex items-center bg-[#05F273] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#04d966] transition-colors duration-200 group"
          >
            <VscArrowRight className="w-0 h-5 opacity-0 group-hover:w-5 group-hover:opacity-100 group-hover:mr-2 transition-all duration-200" />
            {cta_link.text || 'Learn More'}
          </PrismicLink>
        )}

        {/* Features - Alternating Layout */}
        <div className="space-y-8">
          {features.map((feature, index) => {
            const isEven = index % 2 === 0;

            return (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${isEven ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content Column */}
                <div className={`space-y-6 ${isEven ? 'lg:col-start-2' : ''}`}>
                  {/* Feature Heading */}
                  {feature.feature_heading && (
                    <div>
                      <PrismicRichText
                        field={feature.feature_heading}
                        components={{
                          heading3: ({ children }) => (
                            <h3 className="text-[42px] font-bold text-white mb-4 font-antonio">
                              {children}
                            </h3>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* Feature Description */}
                  {feature.feature_description && (
                    <div>
                      <PrismicRichText
                        field={feature.feature_description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-gray-300 leading-relaxed mb-6 font-inter">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* Bullet Points */}
                  {feature.bullet_points && (
                    <div className="space-y-3">
                      <PrismicRichText
                        field={feature.bullet_points}
                        components={{
                          listItem: ({
                            children,
                          }: {
                            children: React.ReactNode;
                          }) => (
                            <div className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-[#05F273] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-[#05F273] leading-relaxed text-left">
                                {children}
                              </span>
                            </div>
                          ),
                          paragraph: ({
                            children,
                          }: {
                            children: React.ReactNode;
                          }) => {
                            // Extract text content from React children
                            const extractText = (
                              node: React.ReactNode,
                            ): string => {
                              if (typeof node === 'string') return node;
                              if (typeof node === 'number') return String(node);
                              if (
                                React.isValidElement(node) &&
                                node.props.children
                              ) {
                                return extractText(node.props.children);
                              }
                              if (Array.isArray(node)) {
                                return node.map(extractText).join('');
                              }
                              return '';
                            };

                            const text = extractText(children);
                            const sentences = text
                              .split(/[.!?]\s+/)
                              .filter((s) => s.trim().length > 0);

                            return (
                              <>
                                {sentences.map((sentence, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-start space-x-3"
                                  >
                                    <div className="w-2 h-2 bg-[#05F273] rounded-full mt-2 flex-shrink-0"></div>
                                    <span className="text-[#05F273] leading-relaxed text-left">
                                      {sentence.trim()}
                                      {sentence.trim().match(/[.!?]$/)
                                        ? ''
                                        : '.'}
                                    </span>
                                  </div>
                                ))}
                              </>
                            );
                          },
                          list: ({
                            children,
                          }: {
                            children: React.ReactNode;
                          }) => <div className="space-y-3">{children}</div>,
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Visual/GIF Column */}
                <div className={`${isEven ? 'lg:col-start-1' : ''}`}>
                  {feature.visual && feature.visual.url ? (
                    <PrismicNextImage
                      field={feature.visual}
                      className="w-full min-h-[200px] sm:min-h-[260px] md:min-h-[300px] object-contain rounded-lg"
                      quality={90}
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
                      unoptimized={false}
                    />
                  ) : (
                    // Default graphics based on index
                    <div className="w-full">
                      {index === 0 ? (
                        <LegacyCodeGraphic />
                      ) : (
                        <VelxWorkflowGraphic />
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureColumnsWithGraphic;
